import React, { useCallback, useEffect, useMemo, useState } from 'react'
import Button from '@material-ui/core/Button'
import TextField from '@material-ui/core/TextField'
import MenuItem from '@material-ui/core/MenuItem'
import IconButton from '@material-ui/core/IconButton'
import Dialog from '@material-ui/core/Dialog'
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward'
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward'
import useTranslation from '../../../Hooks/useTranslation'
import AlertToastr from '../../../utils/alert'
import MmsService from '../../../Services/MMS/mms-api.service'
import DmsService from '../../../Services/DMS/dms-api.service'
import {
  DETAIL_BLOCKS,
  getDetailBlockMeta,
  loadFleetViewChannelSettings,
} from './fleetViewSettingsStorage'

const RemoveIcon = () => <div className="removeIcon" />

const DEFAULT_FLEET_TYPE = 'fracFleet'
const DEFAULT_EQUIPMENT_TYPE = 'Pump'

/** Matches backend FleetType enum order used by /Fleet/standard-channel-fields/{fleetType}. */
const FLEET_TYPE_API_VALUES = {
  fracFleet: 0,
  pumpdownFleet: 1,
  customer: 2,
  yard: 3,
}

/** Matches backend EquipmentType enum order used by POST /Fleet/standard-channel-fields. */
const EQUIPMENT_TYPE_API_VALUES = {
  Pump: 0,
  Blender: 1,
}

const CATEGORY_TO_BLOCK_KEY = {
  Engine: 'engine',
  Transmission: 'transmission',
  Pump: 'pump',
  Hydraulic: 'hydraulic',
  DGB: 'dgb',
  Table: 'table',
  Blender: 'blender',
}

const BLOCK_KEY_TO_CATEGORY = Object.entries(CATEGORY_TO_BLOCK_KEY).reduce((acc, [category, key]) => {
  acc[key] = category
  return acc
}, {})

const toFleetTypeApiValue = (fleetType) => {
  if (fleetType == null || fleetType === '') return null
  if (typeof fleetType === 'number' && Number.isFinite(fleetType)) return fleetType
  const asNumber = Number(fleetType)
  if (!Number.isNaN(asNumber) && String(fleetType).trim() !== '') return asNumber
  const key = String(fleetType)
  if (Object.prototype.hasOwnProperty.call(FLEET_TYPE_API_VALUES, key)) {
    return FLEET_TYPE_API_VALUES[key]
  }
  const match = Object.keys(FLEET_TYPE_API_VALUES).find(
    (name) => name.toLowerCase() === key.toLowerCase()
  )
  return match != null ? FLEET_TYPE_API_VALUES[match] : null
}

const toEquipmentTypeApiValue = (equipmentType) => {
  if (equipmentType == null || equipmentType === '') return null
  if (typeof equipmentType === 'number' && Number.isFinite(equipmentType)) return equipmentType
  const asNumber = Number(equipmentType)
  if (!Number.isNaN(asNumber) && String(equipmentType).trim() !== '') return asNumber
  const key = String(equipmentType)
  if (Object.prototype.hasOwnProperty.call(EQUIPMENT_TYPE_API_VALUES, key)) {
    return EQUIPMENT_TYPE_API_VALUES[key]
  }
  const match = Object.keys(EQUIPMENT_TYPE_API_VALUES).find(
    (name) => name.toLowerCase() === key.toLowerCase()
  )
  return match != null ? EQUIPMENT_TYPE_API_VALUES[match] : null
}

const categoryToBlockKey = (category) => (
  CATEGORY_TO_BLOCK_KEY[category] || String(category || '').toLowerCase()
)

const emptyDraft = () => ({
  id: null,
  fieldId: null,
  fieldName: '',
  order: 0,
  isDashboardField: null,
  isDetailField: null,
  isDefault: false,
})

const PREVIEW_PUMP = 'pump'
const PREVIEW_DETAILS = 'details'
const DEFAULT_DETAIL_CATEGORY = 'Engine'

const BLENDER_KPI_COLORS = ['#3fc6e8', '#5ea8ff', '#7c8cf0', '#46d3c0', '#4a8ef0']

const formatPreviewUnit = (unit) => {
  const value = unit != null ? String(unit).trim() : ''
  return value ? `{${value}}` : '{channel}'
}

const mapApiFieldToChannel = (field, index) => ({
  id: field.id != null ? String(field.id) : `api-${field.fieldName || index}`,
  fieldId: field.id,
  key: field.fieldName,
  label: field.fieldName || '',
  unit: field.unit || '',
  decimals: 1,
  isDefault: true,
  order: field.order ?? index,
})

const FleetViewSettings = () => {
  const translation = useTranslation()
  const [fleetType, setFleetType] = useState(DEFAULT_FLEET_TYPE)
  const [fleetTypeOptions, setFleetTypeOptions] = useState([])
  const [standardChannelFields, setStandardChannelFields] = useState(null)
  const [equipmentType, setEquipmentType] = useState(DEFAULT_EQUIPMENT_TYPE)
  const [previewMode, setPreviewMode] = useState(PREVIEW_PUMP)
  const [category, setCategory] = useState('Table')
  const [settings, setSettings] = useState(() => loadFleetViewChannelSettings())
  const [dialogOpen, setDialogOpen] = useState(false)
  const [draft, setDraft] = useState(emptyDraft())
  const [draftError, setDraftError] = useState('')
  const [isSwappingOrder, setIsSwappingOrder] = useState(false)
  const [isSavingChannel, setIsSavingChannel] = useState(false)
  const [isRemovingChannel, setIsRemovingChannel] = useState(false)
  const [selectedUnitsByField] = useState({})

  const blockKey = useMemo(() => categoryToBlockKey(category), [category])
  const isPumpPreview = previewMode === PREVIEW_PUMP

  useEffect(() => {
    setSettings(loadFleetViewChannelSettings())
  }, [])

  useEffect(() => {
    let cancelled = false
    MmsService.getFleetTypesOptions()
      .then((response) => {
        if (cancelled) return
        const options = response?.data || []
        setFleetTypeOptions(options)
        const hasCurrent = options.some((option) => option.fleetType === fleetType)
        if (!hasCurrent) {
          const frac = options.find(
            (option) => String(option.fleetType || '').toLowerCase() === DEFAULT_FLEET_TYPE.toLowerCase()
          )
          setFleetType(frac?.fleetType || options[0]?.fleetType || DEFAULT_FLEET_TYPE)
        }
      })
      .catch(() => {
        if (!cancelled) setFleetTypeOptions([])
      })
    return () => { cancelled = true }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const apiFleetType = toFleetTypeApiValue(fleetType)
    if (apiFleetType == null) {
      setStandardChannelFields(null)
      return undefined
    }

    let cancelled = false
    DmsService.getFleetStandardChannelFields(apiFleetType)
      .then((response) => {
        if (cancelled) return
        setStandardChannelFields(response?.data ?? null)
      })
      .catch((error) => {
        if (cancelled) return
        setStandardChannelFields(null)
        const message = error?.response?.data?.Message
          || error?.message
          || 'Failed to load standard channel fields'
        AlertToastr.showErrorAlert(message)
      })

    return () => { cancelled = true }
  }, [fleetType])

  const equipmentTypeOptions = useMemo(() => {
    const fromMeta = standardChannelFields?.metadata?.equipmentTypes
    if (Array.isArray(fromMeta) && fromMeta.length) {
      return fromMeta.map((item) => ({
        value: item.item || item.name,
        label: item.name || item.item,
      })).filter((item) => item.value)
    }
    return [
      { value: 'Pump', label: translation.fleetViewSettingsPumpTypeLabel || 'Pump' },
      { value: 'Blender', label: translation.fleetViewSettingsBlenderTypeLabel || 'Blender' },
    ]
  }, [standardChannelFields, translation])

  const allCategoryOptions = useMemo(() => {
    const fields = Array.isArray(standardChannelFields?.fields)
      ? standardChannelFields.fields
      : []
    const metaOrder = Array.isArray(standardChannelFields?.metadata?.categories)
      ? standardChannelFields.metadata.categories
      : []

    const forEquipment = fields
      .filter((field) => String(field.equipmentType || '') === String(equipmentType || ''))
      .map((field) => field.category)
      .filter(Boolean)

    const unique = [...new Set(forEquipment)]
    unique.sort((a, b) => {
      const ai = metaOrder.indexOf(a)
      const bi = metaOrder.indexOf(b)
      if (ai === -1 && bi === -1) return String(a).localeCompare(String(b))
      if (ai === -1) return 1
      if (bi === -1) return -1
      return ai - bi
    })
    return unique
  }, [standardChannelFields, equipmentType])

  const categoryOptions = useMemo(() => {
    if (isPumpPreview) {
      return allCategoryOptions.filter((item) => item === 'Table')
    }
    return allCategoryOptions.filter((item) => item !== 'Table')
  }, [allCategoryOptions, isPumpPreview])

  const apiChannelsForCategory = useMemo(() => {
    const fields = Array.isArray(standardChannelFields?.fields)
      ? standardChannelFields.fields
      : []
    return fields
      .filter((field) => (
        String(field.equipmentType || '') === String(equipmentType || '')
        && String(field.category || '') === String(category || '')
      ))
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
      .map(mapApiFieldToChannel)
  }, [standardChannelFields, equipmentType, category])

  const isPumpEquipment = String(equipmentType || '').toLowerCase() === 'pump'

  useEffect(() => {
    if (!equipmentTypeOptions.length) return
    const hasCurrent = equipmentTypeOptions.some((option) => option.value === equipmentType)
    if (!hasCurrent) {
      const pump = equipmentTypeOptions.find(
        (option) => String(option.value).toLowerCase() === 'pump'
      )
      setEquipmentType(pump?.value || equipmentTypeOptions[0].value)
    }
  }, [equipmentTypeOptions, equipmentType])

  useEffect(() => {
    if (!isPumpEquipment) {
      const preferred = allCategoryOptions[0]
      if (preferred && category !== preferred) setCategory(preferred)
      return
    }
    if (!categoryOptions.length) return
    if (!categoryOptions.includes(category)) {
      const preferred = categoryOptions.includes(DEFAULT_DETAIL_CATEGORY)
        ? DEFAULT_DETAIL_CATEGORY
        : categoryOptions[0]
      setCategory(preferred)
    }
  }, [isPumpEquipment, categoryOptions, category, allCategoryOptions])

  const handlePreviewModeChange = (mode) => {
    setPreviewMode(mode)
    if (mode === PREVIEW_PUMP) {
      setCategory('Table')
      return
    }
    const preferred = allCategoryOptions.includes(DEFAULT_DETAIL_CATEGORY)
      ? DEFAULT_DETAIL_CATEGORY
      : allCategoryOptions.find((item) => item !== 'Table')
    if (preferred) setCategory(preferred)
  }

  const blockMeta = useMemo(() => {
    const mapped = getDetailBlockMeta(blockKey)
    if (mapped && mapped.key === blockKey) return mapped
    return {
      key: blockKey,
      title: category || blockKey,
    }
  }, [blockKey, category])

  const isEditMode = Boolean(draft.id)

  // API is source of truth for order/ids when loaded (Pump Table + Details categories).
  const channels = useMemo(() => {
    if (apiChannelsForCategory.length) return apiChannelsForCategory
    const fromSettings = settings[blockKey]
    if (Array.isArray(fromSettings) && fromSettings.length) return fromSettings
    return []
  }, [settings, blockKey, apiChannelsForCategory])

  const getChannelsForBlockKey = useCallback((key) => {
    const blockCategory = BLOCK_KEY_TO_CATEGORY[key] || key
    const apiFields = Array.isArray(standardChannelFields?.fields)
      ? standardChannelFields.fields
        .filter((field) => (
          String(field.equipmentType || '') === String(equipmentType || '')
          && String(field.category || '') === String(blockCategory || '')
        ))
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
        .map(mapApiFieldToChannel)
      : []
    if (apiFields.length) return apiFields
    const fromSettings = settings[key]
    return Array.isArray(fromSettings) ? fromSettings : []
  }, [standardChannelFields, equipmentType, settings])

  const selectCategoryByBlockKey = useCallback((nextBlockKey) => {
    setPreviewMode(PREVIEW_DETAILS)
    setCategory(
      BLOCK_KEY_TO_CATEGORY[nextBlockKey]
      || (categoryOptions.includes(DEFAULT_DETAIL_CATEGORY) ? DEFAULT_DETAIL_CATEGORY : null)
      || categoryOptions[0]
      || DEFAULT_DETAIL_CATEGORY
    )
  }, [categoryOptions])

  const pumpTilePreview = useMemo(() => {
    const tableChannels = (Array.isArray(settings.table) && settings.table.length)
      ? [...settings.table].sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
      : (Array.isArray(standardChannelFields?.fields)
        ? standardChannelFields.fields
          .filter((field) => (
            String(field.equipmentType || '') === 'Pump'
            && String(field.category || '') === 'Table'
          ))
          .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
          .map(mapApiFieldToChannel)
        : [])

    const mainFields = tableChannels.slice(0, 2)
    const metricFields = tableChannels.slice(2)

    return {
      mainMetrics: mainFields.map((ch) => ({
        key: ch.key,
        label: ch.label,
        display: formatPreviewUnit(selectedUnitsByField[ch.key] || ch.unit),
      })),
      metrics: metricFields.map((ch) => ({
        key: ch.key,
        label: ch.label,
        display: formatPreviewUnit(selectedUnitsByField[ch.key] || ch.unit),
      })),
    }
  }, [settings.table, standardChannelFields, selectedUnitsByField])

  const blenderKpiPreview = useMemo(() => (
    channels.map((ch, index) => ({
      key: ch.key,
      label: ch.label,
      display: formatPreviewUnit(selectedUnitsByField[ch.key] || ch.unit),
      color: BLENDER_KPI_COLORS[index % BLENDER_KPI_COLORS.length],
    }))
  ), [channels, selectedUnitsByField])

  const handleOpenAdd = () => {
    setDraft(emptyDraft())
    setDraftError('')
    setDialogOpen(true)
  }

  const handleCloseDialog = () => {
    if (isSavingChannel) return
    setDialogOpen(false)
    setDraft(emptyDraft())
    setDraftError('')
  }

  const closeDialogNow = () => {
    setDialogOpen(false)
    setDraft(emptyDraft())
    setDraftError('')
  }

  const reloadStandardChannelFields = useCallback(async () => {
    const apiFleetType = toFleetTypeApiValue(fleetType)
    if (apiFleetType == null) return
    const response = await DmsService.getFleetStandardChannelFields(apiFleetType)
    setStandardChannelFields(response?.data ?? null)
    setSettings((prev) => {
      const next = { ...prev }
      delete next[blockKey]
      return next
    })
  }, [fleetType, blockKey])

  const resolveApiFieldId = useCallback((channel) => {
    if (!channel) return null
    if (channel.fieldId != null && Number.isFinite(Number(channel.fieldId))) {
      return Number(channel.fieldId)
    }
    if (channel.id != null && Number.isFinite(Number(channel.id))) {
      return Number(channel.id)
    }
    const fields = Array.isArray(standardChannelFields?.fields)
      ? standardChannelFields.fields
      : []
    const match = fields.find((field) => (
      field.fieldName === channel.key
      && String(field.equipmentType || '') === String(equipmentType || '')
      && String(field.category || '') === String(category || '')
    ))
    return match?.id != null && Number.isFinite(Number(match.id))
      ? Number(match.id)
      : null
  }, [standardChannelFields, equipmentType, category])

  const handleSaveChannel = async () => {
    const fieldName = (draft.fieldName || '').trim()
    if (!fieldName) {
      setDraftError(translation.fleetViewSettingsFieldNameRequired || 'Field name is required')
      return
    }

    const apiFleetType = toFleetTypeApiValue(fleetType)
    const apiEquipmentType = toEquipmentTypeApiValue(equipmentType)
    if (apiFleetType == null || apiEquipmentType == null || !category) {
      AlertToastr.showErrorAlert('Fleet type, equipment type and category are required')
      return
    }

    const isBlender = String(equipmentType || '').toLowerCase() === 'blender'
    const isTable = String(category) === 'Table'
    const isDashboardField = draft.isDashboardField != null
      ? Boolean(draft.isDashboardField)
      : (isTable || isBlender)
    const isDetailField = draft.isDetailField != null
      ? Boolean(draft.isDetailField)
      : (!isTable || isBlender)

    if (isEditMode) {
      const fieldId = Number(draft.fieldId ?? draft.id)
      if (!Number.isFinite(fieldId)) {
        AlertToastr.showErrorAlert('Cannot edit channel: field id is missing')
        return
      }

      setIsSavingChannel(true)
      try {
        const response = await DmsService.updateFleetStandardChannelField(fieldId, {
          fleetType: apiFleetType,
          equipmentType: apiEquipmentType,
          fieldName,
          category,
          isDashboardField,
          isDetailField,
          order: Number(draft.order) || 0,
        })
        const payload = response?.data || {}
        const isSuccessful = payload.isSuccessful ?? payload.IsSuccessful
        const message = payload.message || payload.Message
        if (isSuccessful === false) {
          AlertToastr.showErrorAlert(message || 'Failed to update channel')
          return
        }

        closeDialogNow()
        await reloadStandardChannelFields()
      } catch (error) {
        AlertToastr.showErrorAlert(
          error?.response?.data?.Message
          || error?.response?.data?.message
          || error?.message
          || 'Failed to update channel'
        )
      } finally {
        setIsSavingChannel(false)
      }
      return
    }

    const nextOrder = channels.reduce((max, ch) => {
      const order = Number(ch.order)
      return Number.isFinite(order) && order > max ? order : max
    }, 0) + 1

    setIsSavingChannel(true)
    try {
      const response = await DmsService.createFleetStandardChannelField({
        fleetType: apiFleetType,
        equipmentType: apiEquipmentType,
        fieldName,
        category,
        isDashboardField,
        isDetailField,
        order: nextOrder,
      })
      const payload = response?.data || {}
      const isSuccessful = payload.isSuccessful ?? payload.IsSuccessful
      const message = payload.message || payload.Message
      if (isSuccessful === false) {
        AlertToastr.showErrorAlert(message || 'Failed to add channel')
        return
      }

      closeDialogNow()
      await reloadStandardChannelFields()
    } catch (error) {
      AlertToastr.showErrorAlert(
        error?.response?.data?.Message
        || error?.response?.data?.message
        || error?.message
        || 'Failed to add channel'
      )
    } finally {
      setIsSavingChannel(false)
    }
  }

  const handleRemoveChannel = useCallback(async (id) => {
    if (isRemovingChannel || isSwappingOrder || isSavingChannel) return

    const channel = channels.find((ch) => ch.id === id)
    const fieldId = resolveApiFieldId(channel)
    if (fieldId == null) {
      AlertToastr.showErrorAlert('Cannot delete channel: field id is missing')
      return
    }

    setIsRemovingChannel(true)
    try {
      const response = await DmsService.deleteFleetStandardChannelField(fieldId)
      const payload = response?.data || {}
      const isSuccessful = payload.isSuccessful ?? payload.IsSuccessful
      const message = payload.message || payload.Message
      if (isSuccessful === false) {
        AlertToastr.showErrorAlert(message || 'Failed to delete channel')
        return
      }

      await reloadStandardChannelFields()
    } catch (error) {
      AlertToastr.showErrorAlert(
        error?.response?.data?.Message
        || error?.response?.data?.message
        || error?.message
        || 'Failed to delete channel'
      )
    } finally {
      setIsRemovingChannel(false)
    }
  }, [
    channels,
    isRemovingChannel,
    isSwappingOrder,
    isSavingChannel,
    resolveApiFieldId,
    reloadStandardChannelFields,
  ])

  const handleMove = useCallback(async (id, direction) => {
    if (isSwappingOrder) return

    const list = channels
    const index = list.findIndex((ch) => ch.id === id)
    if (index < 0) return
    const nextIndex = index + direction
    if (nextIndex < 0 || nextIndex >= list.length) return

    const current = list[index]
    const neighbor = list[nextIndex]
    const fieldId1 = resolveApiFieldId(current)
    const fieldId2 = resolveApiFieldId(neighbor)

    if (fieldId1 == null || fieldId2 == null) {
      AlertToastr.showErrorAlert('Cannot change order: field id is missing')
      return
    }

    setIsSwappingOrder(true)
    try {
      const response = await DmsService.swapFleetStandardChannelFieldsOrder({
        fieldId1,
        fieldId2,
      })
      const payload = response?.data || {}
      const isSuccessful = payload.isSuccessful ?? payload.IsSuccessful
      const message = payload.message || payload.Message
      if (isSuccessful === false) {
        AlertToastr.showErrorAlert(message || 'Failed to change channel order')
        return
      }

      await reloadStandardChannelFields()
    } catch (error) {
      AlertToastr.showErrorAlert(
        error?.response?.data?.Message
        || error?.response?.data?.message
        || error?.message
        || 'Failed to change channel order'
      )
    } finally {
      setIsSwappingOrder(false)
    }
  }, [
    channels,
    isSwappingOrder,
    resolveApiFieldId,
    reloadStandardChannelFields,
  ])

  return (
    <div className="pageCard fleetViewSettings">
      <div className="fleetViewSettingsHeader">
        <div className="fleetViewSettingsHeaderMain">
          <TextField
            select
            variant="outlined"
            margin="none"
            label={translation.saveDepartmentModalFleetTypeLabel || 'Fleet Type'}
            value={fleetType}
            onChange={(e) => setFleetType(e?.target?.value || DEFAULT_FLEET_TYPE)}
            id="fleetViewSettingsFleetTypeSelect"
            className="fleetViewSettingsTypeSelect"
          >
            {!fleetTypeOptions.length ? (
              <MenuItem value={DEFAULT_FLEET_TYPE} className="filterMenuItem">
                Frac fleet
              </MenuItem>
            ) : (
              fleetTypeOptions.map((option) => (
                <MenuItem
                  key={option.fleetType}
                  value={option.fleetType}
                  className="filterMenuItem"
                >
                  {option.name}
                </MenuItem>
              ))
            )}
          </TextField>
          <TextField
            select
            variant="outlined"
            margin="none"
            label={translation.fleetViewSettingsTypeLabel || 'Equipment type'}
            value={equipmentType}
            onChange={(e) => setEquipmentType(e?.target?.value || DEFAULT_EQUIPMENT_TYPE)}
            id="fleetViewSettingsEquipmentTypeSelect"
            className="fleetViewSettingsTypeSelect"
          >
            {equipmentTypeOptions.map((option) => (
              <MenuItem
                key={option.value}
                value={option.value}
                className="filterMenuItem"
              >
                {option.label}
              </MenuItem>
            ))}
          </TextField>
          {isPumpEquipment ? (
            <div className={isPumpPreview ? 'optionsSwitch' : 'optionsSwitch in'}>
              <div
                className="option"
                onClick={() => handlePreviewModeChange(PREVIEW_PUMP)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') handlePreviewModeChange(PREVIEW_PUMP)
                }}
                id="fleetViewSettingsPreviewPumpOption"
              >
                {translation.fleetViewSettingsPreviewModeLabel || 'Preview'}
              </div>
              <div
                className="option"
                onClick={() => handlePreviewModeChange(PREVIEW_DETAILS)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') handlePreviewModeChange(PREVIEW_DETAILS)
                }}
                id="fleetViewSettingsPreviewDetailsOption"
              >
                {translation.popoverDetailsLabel || 'Details'}
              </div>
            </div>
          ) : null}
        </div>
        <div className="fleetViewSettingsHeaderActions">
          <Button
            variant="outlined"
            color="primary"
            onClick={handleOpenAdd}
            id="fleetViewSettingsAddChannelBtn"
            disabled={!category}
          >
            {translation.fleetViewSettingsAddChannelLabel || 'Add channel'}
          </Button>
        </div>
      </div>

      <div className="fleetViewSettingsLayout fleetViewSettingsLayout--detail">
        <div className="fleetViewSettingsListPanel">
          <div className="fleetViewSettingsSectionHead">
            <span className="fleetViewSettingsSectionTitle">
              {isPumpEquipment
                ? blockMeta.title
                : (equipmentType || translation.fleetViewSettingsBlenderTypeLabel || 'Blender')}
            </span>
          </div>

          {!channels.length ? (
            <div className="fleetViewSettingsEmpty">
              {translation.fleetViewSettingsNoChannels
                || 'No channels. Add a channel or reset to defaults.'}
            </div>
          ) : (
            <ul className="fleetViewSettingsChannelList">
              {channels.map((ch, index) => (
                <li className="fleetViewSettingsChannelRow" key={ch.id}>
                  <div className="fleetViewSettingsChannelIndex">{index + 1}</div>
                  <div className="fleetViewSettingsChannelBody">
                    <div className="fleetViewSettingsChannelTop">
                      <span className="fleetViewSettingsChannelLabel">{ch.label}</span>
                    </div>
                    {ch.unit ? (
                      <div className="fleetViewSettingsChannelMeta">
                        <span className="fleetViewSettingsChannelUnit">{ch.unit}</span>
                      </div>
                    ) : null}
                  </div>
                  <div className="fleetViewSettingsChannelActions">
                    <IconButton
                      size="small"
                      disabled={index === 0 || isSwappingOrder}
                      onClick={() => handleMove(ch.id, -1)}
                      title={translation.fleetViewSettingsMoveUpLabel || 'Move up'}
                      className="fleetViewSettingsActionBtn"
                    >
                      <ArrowUpwardIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      disabled={index === channels.length - 1 || isSwappingOrder}
                      onClick={() => handleMove(ch.id, 1)}
                      title={translation.fleetViewSettingsMoveDownLabel || 'Move down'}
                      className="fleetViewSettingsActionBtn"
                    >
                      <ArrowDownwardIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleRemoveChannel(ch.id)}
                      title={translation.deleteButtonLabel || 'Remove'}
                      className="fleetViewSettingsActionBtn fleetViewSettingsActionBtn--danger"
                      disabled={isRemovingChannel || isSavingChannel || isSwappingOrder}
                    >
                      <RemoveIcon />
                    </IconButton>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="fleetViewSettingsPreviewPanel">
          <div className="fleetViewSettingsSectionHead">
            <span className="fleetViewSettingsSectionTitle">
              {!isPumpEquipment
                ? (translation.fleetViewSettingsBlenderPreviewLabel || 'Blender process preview')
                : isPumpPreview
                  ? (translation.fleetViewSettingsPumpPreviewLabel || 'Pump tile preview')
                  : (translation.fleetViewSettingsPreviewLabel || 'Pump detail preview')}
            </span>
          </div>

          {!isPumpEquipment ? (
            <div className="fleetViewSettingsPreviewCard fleetViewSettingsBlenderPreview">
              <div className="fdCardToolbar">
                <span className="fdCardToolbarTitle">
                  {translation.fleetProcessTreatmentLabel || 'Process Treatment — live'}
                </span>
              </div>
              {!blenderKpiPreview.length ? (
                <div className="fleetViewSettingsEmpty">
                  {translation.fleetViewSettingsNoChannels
                    || 'No channels. Add a channel or reset to defaults.'}
                </div>
              ) : (
                <ul className="digitalDisplayList">
                  {blenderKpiPreview.map((kpi) => (
                    <li className="digitalDisplayListItem" key={kpi.key}>
                      <h3 className="name">{kpi.label}</h3>
                      <div className="displayContent">
                        <div className="data">
                          <span className="qnt fleetViewSettingsChannelValue">
                            {kpi.display}
                          </span>
                        </div>
                        <div className="statusbar" style={{ backgroundColor: kpi.color }} />
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ) : isPumpPreview ? (
            <div className="fleetViewSettingsPreviewCard">
              <div className="fdPumpTile">
                <div className="fdPumpTileTop">
                  <span className="fdDot" style={{ background: '#46d39a' }} />
                  <div className="fdPumpTileTopText">
                    <span className="fdPumpId">Pump 3</span>
                    <span className="fdPumpTrans">{'{unitUid}'}</span>
                  </div>
                </div>
                <div className="fdPumpMain">
                  {pumpTilePreview.mainMetrics.map((metric, index) => (
                    <div
                      key={metric.key}
                      className={`fdPumpMainRow${index === 0 ? ' fdPumpMainRow--primary' : ''}`}
                    >
                      <span className="fdPumpMainLabel">{metric.label}</span>
                      <span className="fdPumpMainValue fleetViewSettingsChannelValue">
                        {metric.display}
                      </span>
                    </div>
                  ))}
                </div>
                <div className="fdPumpMetrics">
                  {pumpTilePreview.metrics.map((metric) => (
                    <div key={metric.key} className="fdPumpMetricRow">
                      <span>{metric.label}</span>
                      <span className="fleetViewSettingsChannelValue">
                        {metric.display}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div className="fleetViewSettingsDetailPreview">
              <div className="fleetViewSettingsDetailPreviewHead">
                <span className="fleetViewSettingsDetailPreviewTitle">Pump 3</span>
                <span className="fleetViewSettingsDetailPreviewSub">{'{unitUid}'}</span>
              </div>
              <div className="fleetViewSettingsDetailPreviewBody">
                <div className="fleetViewSettingsDetailPreviewGrid">
                  {DETAIL_BLOCKS.filter((b) => b.key !== 'dgb').map((block) => {
                    const fields = getChannelsForBlockKey(block.key)
                    const isActive = block.key === blockKey
                    return (
                      <div
                        key={block.key}
                        className={`fdGroupCard fleetViewSettingsPreviewBlock${isActive ? ' is-active' : ''}`}
                        onClick={() => selectCategoryByBlockKey(block.key)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') selectCategoryByBlockKey(block.key)
                        }}
                      >
                        <div className="fdGroupCardTitle">
                          {block.title}
                        </div>
                        {fields.map((field) => (
                          <div key={field.id || field.key} className="fdGroupCardRow">
                            <span>{field.label}</span>
                            <span className="fleetViewSettingsChannelValue">
                              {formatPreviewUnit(selectedUnitsByField[field.key] || field.unit)}
                            </span>
                          </div>
                        ))}
                      </div>
                    )
                  })}
                </div>
                {(() => {
                  const dgb = DETAIL_BLOCKS.find((b) => b.key === 'dgb')
                  const fields = getChannelsForBlockKey('dgb')
                  const isActive = blockKey === 'dgb'
                  return (
                    <div
                      className={`fdDgb fleetViewSettingsPreviewBlock${isActive ? ' is-active' : ''}`}
                      onClick={() => selectCategoryByBlockKey('dgb')}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') selectCategoryByBlockKey('dgb')
                      }}
                    >
                      <div className="fdGroupCardTitle">
                        {dgb.title}
                      </div>
                      <div className="fdDgbGrid">
                        {fields.map((field) => (
                          <div key={field.id || field.key} className="fdGroupCardRow">
                            <span>{field.label}</span>
                            <span className="fleetViewSettingsChannelValue">
                              {formatPreviewUnit(selectedUnitsByField[field.key] || field.unit)}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )
                })()}
              </div>
            </div>
          )}
        </div>
      </div>

      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        className="respModal autoHeight"
        disableRestoreFocus
      >
        <div className="modalTitleHolder mb16">
          <h1 className="modalTitle">
            {isEditMode
              ? (translation.fleetViewSettingsRenameChannelLabel || 'Rename channel')
              : (translation.fleetViewSettingsAddChannelLabel || 'Add channel')}
          </h1>
        </div>
        <TextField
          autoFocus
          fullWidth
          variant="outlined"
          margin="normal"
          label={translation.fleetViewSettingsFieldNameHeader || 'Field name'}
          value={draft.fieldName ?? ''}
          onChange={(e) => {
            const value = e?.target?.value ?? ''
            setDraftError('')
            setDraft((prev) => ({ ...(prev || emptyDraft()), fieldName: value }))
          }}
          id="fleetViewSettingsChannelFieldName"
          error={Boolean(draftError)}
          helperText={draftError || undefined}
          disabled={isSavingChannel}
        />
        <div className="btnHolder end mt22 fixedWidthBtn">
          <Button
            onClick={handleCloseDialog}
            variant="outlined"
            disabled={isSavingChannel}
          >
            {translation.cancelButtonLabel || 'Cancel'}
          </Button>
          <Button
            onClick={handleSaveChannel}
            color="primary"
            variant="contained"
            disabled={isSavingChannel}
          >
            {isEditMode
              ? (translation.saveButtonLabel || 'Save')
              : (translation.addButtonLabel || 'Add')}
          </Button>
        </div>
      </Dialog>

    </div>
  )
}

export default FleetViewSettings
